import { useEffect, useRef, useState } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { ProductImageZoom } from '@/pages/fulfillment/components/ProductImageZoom';
import type { PurchaseOrderCandidate } from '@/infrastructure/FulfillmentAdminClient';

export interface PurchaseOrderLookupModalProps {
  /** The code the desk scanned and nothing placed; it is the tracking number a match registers. */
  code: string;
  busy: boolean;
  onLookup: (orderNo: string) => Promise<PurchaseOrderCandidate[]>;
  onMatch: (candidate: PurchaseOrderCandidate) => void;
  /** Hands the parcel on to the pool, carrying the number that failed when there was one. */
  onSendToPool: (orderNo: string | null) => void;
  onClose: () => void;
}

/** How a product reads on every fulfillment screen: brand first, Korean underneath [G-6]. */
const productLabel = (candidate: PurchaseOrderCandidate): string =>
  [candidate.brand, candidate.productName].filter(Boolean).join(' ') || candidate.sku;

/**
 * M2 — the order-number lookup a desk gets when a scanned barcode placed nothing [L-M2]. The operator
 * can still read the purchase order number off the parcel, and matching a row registers the scanned
 * code onto that order line, so rescanning the same barcode resolves normally [BR-27]. Only a lookup
 * that finds nothing hands the parcel on to the pool.
 */
export function PurchaseOrderLookupModal({
  code,
  busy,
  onLookup,
  onMatch,
  onSendToPool,
  onClose,
}: PurchaseOrderLookupModalProps) {
  const [orderNo, setOrderNo] = useState('');
  const [candidates, setCandidates] = useState<PurchaseOrderCandidate[] | null>(null);
  const [searched, setSearched] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const dialogRef = useModalDialog(onClose);
  const orderNoRef = useRef<HTMLInputElement>(null);
  /** Only the newest lookup may paint; an earlier one landing late would offer rows from another order. */
  const attempt = useRef(0);

  // The operator's hands are already on the keyboard; the number is the only thing this modal wants.
  useEffect(() => orderNoRef.current?.focus(), []);

  const lookUp = async () => {
    const entered = orderNo.trim();
    if (entered.length === 0) {
      return;
    }
    // Enter pressed again while the same number is still being looked up starts nothing new.
    if (searched === entered && candidates === null && !failed) {
      return;
    }
    const mine = ++attempt.current;
    setSearched(entered);
    setFailed(false);
    setCandidates(null);
    try {
      const found = await onLookup(entered);
      if (mine === attempt.current) {
        setCandidates(found);
      }
    } catch {
      // An outage is not a confirmed miss: routing a valid order to the pool would lose the parcel.
      if (mine === attempt.current) {
        setFailed(true);
      }
    }
  };

  const found = candidates !== null && candidates.length > 0;
  const missed = candidates !== null && candidates.length === 0;

  return (
    <dialog className="vo-modal" ref={dialogRef} aria-label="Barcode Not Recognized">
      <header>
        <b>Barcode Not Recognized</b>
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <p>No order matches the scanned barcode {code}.</p>
        <p>
          Enter the Coupang purchase order number to find matching products and register the tracking number on the
          spot.
        </p>
        <div className="row">
          <label>
            <span>Order No</span>
            <input
              ref={orderNoRef}
              aria-label="Order No"
              value={orderNo}
              onChange={(event) => {
                setOrderNo(event.target.value);
                // A number being edited belongs to no result: the rows on screen were placed by the old one, and a
                // lookup still in flight for it must not paint either, so a stale line cannot be matched.
                if (searched !== null) {
                  attempt.current += 1;
                  setSearched(null);
                  setCandidates(null);
                  setFailed(false);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void lookUp();
                }
              }}
            />
          </label>
          <button type="button" className="btn btn-line btn-sm" disabled={busy} onClick={() => void lookUp()}>
            Look up
          </button>
        </div>

        {found && (
          <div>
            <p>
              Order number {searched} matched {candidates.length} products — click the match button on the scanned
              product’s row
            </p>
            <table className="tbl" aria-label="Matching product lines">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Product Name</th>
                  <th>Order</th>
                  <th>Qty</th>
                  <th>Tracking No</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => (
                  <tr key={`${candidate.orderId}-${candidate.lineNo}`}>
                    <td>
                      {candidate.imageUrl && (
                        <ProductImageZoom src={candidate.imageUrl} label={productLabel(candidate)}>
                          <img src={candidate.imageUrl} alt={productLabel(candidate)} width={40} height={40} />
                        </ProductImageZoom>
                      )}
                    </td>
                    <td>
                      <b>{productLabel(candidate)}</b>
                      {candidate.productNameKr && <div>{candidate.productNameKr}</div>}
                    </td>
                    <td>{candidate.orderId}</td>
                    <td>{candidate.quantity}</td>
                    <td>{candidate.trackingNumber ?? '—'}</td>
                    <td>
                      {candidate.trackingNumber ? (
                        <span>Already registered</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-green btn-sm"
                          disabled={busy}
                          onClick={() => onMatch(candidate)}
                        >
                          Match Tracking No
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              Clicking match registers the tracking number on that line and closes this window — rescanning the same
              barcode is then recognized normally.
            </p>
          </div>
        )}

        {failed && <p>The order number could not be searched — try again before sending the parcel to the pool.</p>}

        {missed && (
          <div>
            <p>
              No products match the entered order number (possible typo or a number from another channel). Send it to
              the Missing Tracking List.
            </p>
            <button type="button" className="btn btn-line btn-sm" onClick={() => onSendToPool(searched)}>
              Send to Missing Tracking List
            </button>
          </div>
        )}
      </div>
      <footer>
        <button type="button" className="btn btn-line btn-sm" onClick={() => onSendToPool(null)}>
          No order number
        </button>
        <button type="button" className="btn btn-line btn-sm" onClick={onClose}>
          Cancel
        </button>
      </footer>
    </dialog>
  );
}
